import React, { FC, MouseEvent, PropsWithChildren } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      margin: theme.spacing(1)
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    button: {
      margin: theme.spacing(1)
    }
  };
});

interface SettingsCardProps {
  title: string;
  description: string;
  expanded: string | null;
  panel: string;
  applyLabel?: string;
  disabled?: boolean;
  handleChangeExpanded: (panel: string | null) => () => void;
  onCancel?: (event?: MouseEvent<HTMLButtonElement>) => void;
  onSubmit: (event?: MouseEvent<HTMLButtonElement>) => void;
  closeOnSubmit?: boolean;
}

const SettingsCard: FC<PropsWithChildren<SettingsCardProps>> = ({
  title,
  description,
  expanded,
  panel,
  applyLabel,
  children,
  disabled = false,
  handleChangeExpanded,
  onCancel,
  onSubmit,
  closeOnSubmit = true
}) => {
  const classes = useStyles();
  const { t } = useTranslation('SettingsCard');

  const handleCancelClick = (): void => {
    handleChangeExpanded(null)();
    if (onCancel) onCancel();
  };

  const handleSubmitClick = (): void => {
    if (closeOnSubmit) handleChangeExpanded(null)();
    onSubmit();
  };

  return (
    <Card className={classes.root} elevation={2}>
      <CardHeader
        title={title}
        subheader={description}
        action={
          <IconButton
            data-testid="collapse-settings"
            aria-label={t('Collapse settings')}
            onClick={handleChangeExpanded(panel)}
          >
            {expanded === panel ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        }
      />
      <Collapse in={expanded === panel} timeout="auto" unmountOnExit>
        <CardContent>
          <div>{children}</div>
          <div className={classes.footer}>
            <Button
              variant="outlined"
              className={classes.button}
              color="secondary"
              size="small"
              disableElevation
              onClick={handleCancelClick}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              className={classes.button}
              color="primary"
              size="small"
              disableElevation
              disabled={disabled}
              onClick={handleSubmitClick}
            >
              {applyLabel ? applyLabel : t('Apply Changes')}
            </Button>
          </div>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default SettingsCard;
